import { ChevronRight } from 'lucide-react';
import { Avatar, Card, Typography } from '../ui';
import { ChannelChip } from './ChannelChip';
import { StageDot } from './StageDot';
import { StageLine } from './StageLine';
import { StageQuestion } from './StageCard';
import { inboxPreviewAuthor, inboxRowFirstName, inboxRowName, inboxRowTime, type InboxRow as InboxRowData } from '../../../lib/contact-rows';
import { useAgentName } from '../../../lib/use-agent-name';
import { messageDir } from '../../../lib/message-direction';
import { avatarInitials, avatarSlotFor } from '../../../lib/avatar-color';
import type { WebContactCard } from '../../../lib/contact-book';
import { stageBiValue, stageCard } from '../../../lib/outreach-stage';
import { useGenericBi } from '../../../lib/use-generic-bi';
import { useNarrow } from '../../../lib/use-narrow';
import { useTranslation } from '../../../lib/i18n';
import styles from './contacts.legacy.module.css';

/**
 * One Inbox row — the last message of a contact on ONE channel. Tapping it opens the outreach viewer
 * on `{ personId, channel }`, exactly like the contact detail's "Needs you" rows. Shared 1:1 with
 * iOS `InboxRowView` and Android `InboxRow`.
 *
 * Two per-viewport anatomies from Figma (web `17937:269511`, native `17937:265619`):
 *  - **desktop** — avatar · [stage dot + name + `ChannelChip` / message preview / the stage line]. Card `lg`.
 *  - **compact** (mobile-web + phones) — avatar · [stage dot + name / message preview / the stage line /
 *    chip] · last-activity time + chevron. Card `sm`.
 *
 * The time is `inboxRowTime` (today → "08:00", this week → "Mon", older → "Oct 3"), end-aligned and never
 * wrapping; on desktop it closes the name row. The preview wears its author (`inboxPreviewAuthor`) —
 * "The Maverick: …" / "Dana: …" / "Dana's agent: …" — off the thread's `lastAuthor`. Kept 1:1 with the
 * redesign's `InboxRow.tsx` (the legacy body is what most owners still see).
 *
 * A NEEDS-YOU row with an open Decision (Q9) swaps the preview for "{channel} · {agent} is asking you"
 * and carries the shared prompt block BELOW the card, as its sibling — the card is a button, and the
 * block's chips are buttons (Q3).
 */
export function InboxRow({
  row,
  onOpen,
}: {
  row: InboxRowData<WebContactCard>;
  /** Open the conversation by the row's identity (person or, address-only, recipient) + outreach. */
  onOpen: (personId: string, recipientId: string | undefined, outreachId: string) => void;
}) {
  const { t, i18n } = useTranslation();
  const agent = useAgentName();
  const { logPageClickBi } = useGenericBi();
  const narrow = useNarrow();
  const card = row.contact;
  // Through the shared rule, which applies the ADDRESS fallback: the row used to pass no channels, so
  // every address-only contact read "Unnamed contact" — indistinguishable from every other one.
  const displayName = inboxRowName(row, t('contacts.row.unnamed'));
  const asking = stageCard(row.thread)?.kind === 'question';
  // When the conversation last moved — today's time, a weekday this week, else a short date.
  const time = inboxRowTime(row.lastActivityAt, new Date(), i18n.language);
  // Who wrote the preview — "The Maverick: …", "Dana: …", "Dana's agent: …", or nothing when unknown.
  const firstName = inboxRowFirstName(row, t('contacts.row.unnamed'));
  const author = inboxPreviewAuthor(row.thread.lastAuthor, {
    agent: agent.name,
    contact: firstName,
    contactAgent: t('outreach.inbox.preview.contactAgent', { name: firstName }),
  });

  const open = () => {
    // person vs address-only recipient (#10660), so recipientId-only opens are distinguishable.
    logPageClickBi(
      { page_name: 'contacts_page', button_name: 'open_inbox_row' },
      { additionalFields: { contact_kind: row.personId ? 'person' : 'recipient', stage: stageBiValue(row.stage) } },
    );
    onOpen(row.personId, row.recipientId, row.outreachId);
  };

  const avatar = (
    <Avatar
      initials={avatarInitials(displayName)}
      photoUrl={card?.effectiveAvatarUrl}
      size={40}
      color={avatarSlotFor(row.personId || row.recipientId || displayName)}
    />
  );
  const nameRow = (
    <span className={styles.inboxNameRow}>
      <StageDot stage={row.stage} />
      <Typography variant="h4" color="primary" className={styles.rowName}>
        {displayName}
      </Typography>
    </span>
  );
  // The channel chip is the DS OUTLINE badge (Figma 17937:265653), placed at the BOTTOM of the text
  // column, under the preview — the same anatomy as the native inbox card.
  const chip = <ChannelChip channel={row.channel} outlined />;
  // A Needs-you card's text IS the question, which the block below prints — so the preview steps aside.
  const preview = !asking && row.lastMessage ? (
    // User/LLM text: MAJORITY-script direction (native `messageIsRtl`); the author prefix rides inside it.
    <Typography
      variant="p3"
      color="secondary"
      className={narrow ? styles.inboxPreviewCompact : styles.inboxPreview}
      dir={messageDir(row.lastMessage)}
    >
      {author ? <span className={styles.previewAuthor}>{`${author}: `}</span> : null}
      {row.lastMessage}
    </Typography>
  ) : null;
  const stageLine = asking ? null : <StageLine thread={row.thread} contactName={displayName} />;

  const rowCard = (
    <Card
      variant="sunken"
      size={narrow ? 'sm' : 'lg'}
      component="button"
      interactive
      className={narrow ? styles.inboxRowCompact : styles.inboxRow}
      onClick={open}
    >
      {narrow ? (
        // Compact (mobile-web = native frame): avatar · [dot + name / preview / stage line / chip] ·
        // last-activity time + chevron.
        <>
          <span className={styles.inboxCompactBody}>
            {avatar}
            <span className={styles.inboxCompactMain}>
              {nameRow}
              {preview}
              {stageLine}
              {chip}
            </span>
            <span className={styles.rowEnd}>
              <Typography variant="p3" color="secondary" className={styles.rowAge}>
                {time}
              </Typography>
              <ChevronRight size={24} className="dir-flip" aria-hidden />
            </span>
          </span>
        </>
      ) : (
        // Desktop (Figma 18480:1081): avatar · [dot + name + chip / preview / stage line]. Here the chip
        // sits NEXT TO the name (unlike the native/mobile-web frame, where it's at the bottom of the column).
        <>
          {avatar}
          <span className={styles.inboxMain}>
            <span className={styles.inboxNameRow}>
              <StageDot stage={row.stage} />
              <Typography variant="h4" color="primary" className={styles.rowName}>
                {displayName}
              </Typography>
              {chip}
              {time ? (
                <Typography variant="p3" color="secondary" className={styles.inboxTime}>
                  {time}
                </Typography>
              ) : null}
            </span>
            {preview}
            {stageLine}
          </span>
        </>
      )}
    </Card>
  );
  if (!asking) return rowCard;
  return (
    <>
      {rowCard}
      <StageQuestion thread={row.thread} page="contacts_page" header="channel" />
    </>
  );
}
